import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Paper,
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  AccessTime,
  Facebook,
  Instagram,
  WhatsApp,
} from '@mui/icons-material';

const contactInfo = [
  {
    icon: <Phone fontSize="large" />,
    title: 'Phone',
    content: ['+91 9847005764'],
  },
  {
    icon: <Email fontSize="large" />,
    title: 'Email',
    content: ['info@eastfortholidays.com'],
  },
  {
    icon: <LocationOn fontSize="large" />,
    title: 'Address',
    content: ['J64R+FP6 Killannur, Kerala', 'Near Poomala Dam'],
  },
  {
    icon: <AccessTime fontSize="large" />,
    title: 'Hours',
    content: ['Check-in: 12:00 PM', 'Check-out: 11:00 AM', '24/7 Front Desk'],
  },
];

const socialMedia = [
  { icon: <Facebook />, name: 'Facebook', url: '#' },
  { icon: <Instagram />, name: 'Instagram', url: '#' },
  { icon: <WhatsApp />, name: 'WhatsApp', url: '#' },
];

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleWhatsAppSubmit = (e) => {
    e.preventDefault();

    // Validate form
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error',
      });
      return;
    }

    // Create the message text for WhatsApp
    const messageText = `*New Contact Form Submission*\n\n` +
      `*Name:* ${formData.name}\n` +
      `*Email:* ${formData.email}\n` +
      `*Phone:* ${formData.phone}\n` +
      `*Subject:* ${formData.subject}\n` +
      `*Message:* ${formData.message}`;

    // Format the phone number (replace with your business WhatsApp number)
    const phoneNumber = '919847005764'; // Add your WhatsApp number here (with country code)
    
    // Encode the message for URL
    const encodedMessage = encodeURIComponent(messageText);
    
    // Detect if device is mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    // Choose appropriate WhatsApp URL
    const whatsappURL = isMobile
      ? `whatsapp://send?phone=${phoneNumber}&text=${encodedMessage}`
      : `https://web.whatsapp.com/send?phone=${phoneNumber}&text=${encodedMessage}`;
    
    // Open WhatsApp in a new window
    window.open(whatsappURL, '_blank');

    // Show success message
    setSnackbar({
      open: true,
      message: 'Redirecting to WhatsApp...',
      severity: 'success',
    });

    // Clear the form
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    });
  };

  return (
    <Box sx={{ py: 8 }}>
      <Container>
        <Typography variant="h3" component="h1" textAlign="center" gutterBottom>
          Contact Us
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" paragraph>
          We're here to help and answer any questions you might have
        </Typography>

        {/* Contact Information Cards */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {contactInfo.map((info) => (
            <Grid item xs={12} sm={6} md={3} key={info.title}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {info.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {info.title}
                  </Typography>
                  {info.content.map((line, index) => (
                    <Typography key={index} variant="body2" color="text.secondary">
                      {line}
                    </Typography>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={4}>
          {/* Contact Form */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom>
                Send us a Message
              </Typography>
              <form onSubmit={handleWhatsAppSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message"
                      name="message"
                      multiline
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      startIcon={<WhatsApp />}
                    >
                      Send Message via WhatsApp
                    </Button>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      By clicking "Send Message", you will be redirected to WhatsApp to send your message.
                    </Typography>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>

          {/* Social Media and Map */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 4, mb: 4 }}>
              <Typography variant="h5" gutterBottom>
                Follow Us
              </Typography>
              <Grid container spacing={2}>
                {socialMedia.map((social) => (
                  <Grid item xs={3} key={social.name}>
                    <Button
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ minWidth: 0 }}
                    >
                      {social.icon}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            <Paper sx={{ p: 4 }}>
  <Typography variant="h5" gutterBottom>
    Location
  </Typography>
  <Box sx={{ height: '300px', width: '100%', mt: 2 }}>
    <iframe
      title="Resort Location"
      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3921.6559505898135!2d76.2416585!3d10.6060495!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba7e9d74f2cdc47%3A0xe8764288da5e505d!2sEastfort%20Holidays%20Resort!5e0!3m2!1sen!2sin!4v1737570283716!5m2!1sen!2sin"
      width="100%"
      height="100%"
      style={{ border: 0 }}
      allowFullScreen=""
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
    />
  </Box>
</Paper>
          </Grid>
        </Grid>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}

export default Contact;